import type { components } from "@roastery/api-client";
import { useMutation } from "@tanstack/react-query";

/** What a public register answered about one company (US-40.1). */
export type CompanyLookup = components["schemas"]["CompanyLookup"];

/**
 * Exactly one of the two, which is what every lookup endpoint requires: nine digits could be a REGON
 * or a NIP with its last digit missing, and nothing guesses — the field somebody typed into is the
 * answer.
 */
export interface RegistryQuery {
  taxId?: string;
  regon?: string;
}

/**
 * How one screen reaches its own endpoint. Each of the four passes the call itself rather than a
 * path (US-40.4): `openapi-fetch` types its answer off the literal path, and a union of the four
 * paths defeats that inference — `Type instantiation is excessively deep`, found the direct way.
 */
export type RegistryFetch = (query: RegistryQuery) => Promise<{ data?: CompanyLookup; error?: unknown }>;

/**
 * Asks a register and hands back what it said (US-40.1), with the four screens sharing one piece of
 * error handling.
 *
 * <p>A mutation rather than a query with `enabled: false`: the number is decided when the button is
 * pressed, and `refetch` would use the parameters of the last render instead. Nothing is cached on
 * purpose — the answer is an offer somebody is about to edit, and a second press means "ask again".
 */
export function useRegistryLookup(fetcher: RegistryFetch) {
  return useMutation({
    mutationFn: async (query: RegistryQuery): Promise<CompanyLookup> => {
      const { data, error } = await fetcher(query);
      if (error || !data) {
        // The problem document itself, so `useApiErrorMessage` can translate it by its code
        // (SALES_INVALID_TAX_ID, SALES_INVALID_REGON) rather than show the backend's log sentence.
        throw error ?? new Error("registry lookup failed");
      }
      return data;
    },
  });
}
